import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Collapse
} from '@mui/material';
import {
  Dashboard,
  People,
  EventNote,
  MonitorHeart,
  LocalHospital,
  History,
  Settings,
  Logout,
  Menu,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  Search,
  Notifications,
  AccountCircle,
  AdminPanelSettings,
  Backup,
  CloudDownload,
  CloudUpload,
} from '@mui/icons-material';
import { useDoctor } from '../contexts/DoctorContext';
import { useAuth } from '../contexts/AuthContext';
import { createBackup, restoreFromBackup, checkAndProposeBackup } from '../services/backupService';
import { useFeedback } from './EnhancedFeedbackProvider';

const drawerWidth = 280;
const collapsedWidth = 64;

const Sidebar = ({ open, onToggle, onEditDoctor }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { doctorName } = useDoctor();
  const { logout } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const [expandedItems, setExpandedItems] = useState({});
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Tableau de Bord',
      icon: <Dashboard />,
      path: '/',
      color: 'primary'
    },
    {
      id: 'patients',
      label: 'Patients',
      icon: <People />,
      path: '/patients',
      color: 'secondary'
    },
    {
      id: 'consultations',
      label: 'Consultations',
      icon: <EventNote />,
      path: '/consultations',
      color: 'info'
    },
    {
      id: 'vitals',
      label: 'Constantes Vitales',
      icon: <MonitorHeart />,
      path: '/vitals',
      color: 'error'
    },
    {
      id: 'treatments',
      label: 'Traitements',
      icon: <LocalHospital />,
      path: '/treatments',
      color: 'warning'
    },
    {
      id: 'history',
      label: 'Antécédents',
      icon: <History />,
      path: '/history',
      color: 'success'
    },
  ];

  // Note: User management removed as we no longer have authentication

  const handleItemClick = (path) => {
    navigate(path);
    if (isMobile) {
      onToggle();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCreateBackup = async () => {
    setIsCreatingBackup(true);
    try {
      const result = await createBackup();
      if (result.success) {
        showSuccess(`Sauvegarde créée: ${result.filename}`);
      } else {
        showError(result.message);
      }
    } catch (error) {
      showError('Erreur lors de la création de la sauvegarde');
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const handleRestoreBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsRestoring(true);
    try {
      const result = await restoreFromBackup(file);
      if (result.success) {
        showSuccess(`Données restaurées avec succès (${result.restoredDataTypes.length} types de données)`);
        // Recharger la page pour appliquer les changements
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        showError(result.message);
      }
    } catch (error) {
      showError('Erreur lors de la restauration');
    } finally {
      setIsRestoring(false);
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header avec Logo */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'center',
          minHeight: 64,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {open && (
          <Box 
            display="flex" 
            alignItems="center"
            onClick={() => navigate('/')}
            sx={{ 
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
          >
            <Avatar
              sx={{
                mr: 2,
                width: 40,
                height: 40,
                bgcolor: 'transparent',
              }}
            >
              <img 
                src="/logo.jpeg" 
                alt="AFDT Logo" 
                style={{ 
                  height: '100%', 
                  width: '100%',
                  borderRadius: '50%',
                  objectFit: 'cover'
                }} 
              />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                AFDT-SUIVIMED
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Plateforme Médicale
              </Typography>
            </Box>
          </Box>
        )}
        
        {!open && (
          <Tooltip title="AFDT-SUIVIMED - Aller au Dashboard" placement="right">
            <IconButton 
              onClick={() => navigate('/')}
              size="small"
              sx={{ 
                mb: 1,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white'
                }
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'transparent',
                }}
              >
                <img 
                  src="/logo.jpeg" 
                  alt="AFDT Logo" 
                  style={{ 
                    height: '100%', 
                    width: '100%',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }} 
                />
              </Avatar>
            </IconButton>
          </Tooltip>
        )}
        
        <IconButton onClick={onToggle} size="small">
          {open ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </Box>

      {/* Menu Principal */}
      <List sx={{ flexGrow: 1, px: 1, py: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
            <Tooltip title={!open ? item.label : ''} placement="right">
              <ListItemButton
                onClick={() => handleItemClick(item.path)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  backgroundColor: isActive(item.path) 
                    ? `${theme.palette[item.color].main}15` 
                    : 'transparent',
                  color: isActive(item.path) 
                    ? theme.palette[item.color].main 
                    : 'text.primary',
                  '&:hover': {
                    backgroundColor: isActive(item.path)
                      ? `${theme.palette[item.color].main}25`
                      : 'action.hover',
                  },
                  minHeight: 48,
                  px: 2,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: open ? 40 : 'auto',
                    color: isActive(item.path) 
                      ? theme.palette[item.color].main 
                      : 'text.secondary',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: isActive(item.path) ? 600 : 500,
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      <Divider />

      {/* Menu Utilisateur */}
      <Box sx={{ p: 2 }}>
        {open ? (
          <Box>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar
                sx={{
                  bgcolor: 'secondary.main',
                  mr: 2,
                  width: 40,
                  height: 40,
                }}
              >
                <AccountCircle />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {doctorName || 'Médecin non défini'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Médecin
                </Typography>
              </Box>
            </Box>
            
            <List dense>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={onEditDoctor}
                  sx={{ borderRadius: 2, mb: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Settings />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Changer le nom"
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  onClick={handleCreateBackup}
                  disabled={isCreatingBackup}
                  sx={{ borderRadius: 2, mb: 0.5, color: 'primary.main' }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                    <CloudDownload />
                  </ListItemIcon>
                  <ListItemText 
                    primary={isCreatingBackup ? "Sauvegarde..." : "Sauvegarder"}
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <input
                  type="file"
                  accept=".zip"
                  onChange={handleRestoreBackup}
                  style={{ display: 'none' }}
                  id="restore-file-input-sidebar"
                />
                <ListItemButton
                  component="label"
                  htmlFor="restore-file-input-sidebar"
                  disabled={isRestoring}
                  sx={{ borderRadius: 2, mb: 0.5, color: 'info.main' }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'info.main' }}>
                    <CloudUpload />
                  </ListItemIcon>
                  <ListItemText 
                    primary={isRestoring ? "Restauration..." : "Restaurer"}
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </ListItemButton>
              </ListItem>
              
              <ListItem disablePadding>
                <ListItemButton
                  onClick={handleLogout}
                  sx={{ borderRadius: 2, color: 'error.main' }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
                    <Logout />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Déconnexion"
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            <Tooltip title="Changer le nom" placement="right">
              <IconButton
                onClick={onEditDoctor}
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <Settings />
              </IconButton>
            </Tooltip>

            <Tooltip title="Sauvegarder" placement="right">
              <IconButton
                onClick={handleCreateBackup}
                disabled={isCreatingBackup}
                size="small"
                sx={{ color: 'primary.main' }}
              >
                <CloudDownload />
              </IconButton>
            </Tooltip>

            <Tooltip title="Restaurer" placement="right">
              <IconButton
                component="label"
                htmlFor="restore-file-input-sidebar-collapsed"
                disabled={isRestoring}
                size="small"
                sx={{ color: 'info.main' }}
              >
                <CloudUpload />
              </IconButton>
            </Tooltip>
            <input
              type="file"
              accept=".zip"
              onChange={handleRestoreBackup}
              style={{ display: 'none' }}
              id="restore-file-input-sidebar-collapsed"
            />
            
            <Tooltip title="Déconnexion" placement="right">
              <IconButton
                onClick={handleLogout}
                size="small"
                sx={{ color: 'error.main' }}
              >
                <Logout />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={onToggle}
      sx={{
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : collapsedWidth,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
